import type { CategoryContent } from './row.js';

export const AUSTRIAN_HISTORY: CategoryContent = {
  bscba: [
    [
      'Which dynasty ruled Austria before the Habsburgs took control in the late 13th century?',
      [
        'Babenberg',
        'Salian',
        'Ottonian',
        'Carolingian',
      ],
      0,
      'The Babenbergs ruled the March and Duchy of Austria from ~976 until 1246, when the line ended. The Habsburgs later emerged as the dominant dynasty.',
    ],
    [
      'What year did the Austrian-Hungarian Empire formally dissolve?',
      [
        '1918',
        '1920',
        '1919',
        '1922',
      ],
      0,
      'Austria-Hungary collapsed in 1918 following defeat in World War I. The Treaty of Saint-Germain in 1919 formally ended the empire.',
    ],
    [
      'Who was the last reigning Emperor of Austria-Hungary?',
      [
        'Francis Joseph I',
        'Charles I',
        'Maximilian I',
        'Ferdinand I',
      ],
      0,
      'Charles I was the last emperor, reigning from 1916 until his abdication in November 1918 as the empire collapsed.',
    ],
    [
      'In what year did Austria declare itself a republic after the empire\'s collapse?',
      [
        '1918',
        '1919',
        '1920',
        '1917',
      ],
      0,
      'Austria declared itself a federal republic in 1918 following the dissolution of Austria-Hungary, which became the First Austrian Republic.',
    ],
    [
      'What major political event occurred in Austria in 1938?',
      [
        'Anschluss with Nazi Germany',
        'Declaration of independence from Hungary',
        'Restoration of the Habsburg monarchy',
        'Election of the first republic president',
      ],
      0,
      'The Anschluss united Austria with Nazi Germany in March 1938, ending Austria\'s independence until after World War II.',
    ],
    [
      'What was the capital of the medieval Duchy of Austria under Babenberg rule?',
      [
        'Vienna',
        'Salzburg',
        'Graz',
        'Linz',
      ],
      0,
      'Vienna served as the capital of the Duchy of Austria under the Babenbergs and remained the principal Habsburg seat thereafter.',
    ],
    [
      'Which treaty formally partitioned Austria-Hungary after World War I?',
      [
        'Saint-Germain',
        'Treaty of Versailles',
        'Treaty of Trianon',
        'Treaty of Brest-Litovsk',
      ],
      0,
      'The Treaty of Saint-Germain (1919) formally dissolved Austria-Hungary and established Austria as a small independent republic.',
    ],
    [
      'In what year did Austria regain full sovereignty and declare permanent neutrality?',
      [
        '1955',
        '1945',
        '1950',
        '1960',
      ],
      0,
      'Austria regained full independence in 1955 and simultaneously declared perpetual neutrality, a status recognized internationally.',
    ],
    [
      'What religious affiliation dominated the Austrian Empire throughout most of its history?',
      [
        'Roman Catholicism',
        'Lutheran Protestantism',
        'Eastern Orthodoxy',
        'Calvinism',
      ],
      0,
      'The Austrian Habsburg state was predominantly Catholic, and Catholicism remained central to Austrian identity and governance.',
    ],
    [
      'Which region was the original power base of the Babenberg dynasty?',
      [
        'The March of Austria',
        'The Alpine valleys of Tyrol',
        'The plains of Moravia',
        'The forests of Bohemia',
      ],
      0,
      'The Babenbergs began as margraves of the March of Austria, a frontier region along the Danube River established as a defensive buffer.',
    ],
    [
      'In what year did Austria join the European Union?',
      [
        '1995',
        '1992',
        '2000',
        '1998',
      ],
      0,
      'Austria joined the European Union in 1995, ending decades of official neutrality in a modified form through EU membership.',
    ],
    [
      'What name was given to the Austrian state after World War I and before World War II?',
      [
        'First Austrian Republic',
        'Second Austrian Republic',
        'Austrian Confederation',
        'Austrian Federal State',
      ],
      0,
      'The period from 1918 to 1938 is known as the First Austrian Republic, ending with the Nazi annexation.',
    ],
    [
      'Which major European city served as the capital of Austria throughout the Habsburg era?',
      [
        'Vienna',
        'Prague',
        'Budapest',
        'Trieste',
      ],
      0,
      'Vienna was the capital of the archduchy, then empire, serving as the Habsburg dynasty\'s principal seat of power.',
    ],
    [
      'What event in 1246 ended Babenberg rule in Austria?',
      [
        'Death of the last Babenberg duke',
        'Invasion by Hungarian forces',
        'Conquest by the Holy Roman Emperor',
        'Peasant rebellion',
      ],
      0,
      'The death of Duke Frederick II (Frederick the Warrior) in 1246 ended the Babenberg line, opening a succession struggle.',
    ],
    [
      'How did Austria emerge from Nazi occupation after World War II?',
      [
        'Liberated by Allied forces, later became neutral',
        'It was immediately partitioned among neighboring states',
        'It remained divided like Germany until 1990',
        'Austria unified voluntarily with West Germany',
      ],
      0,
      'Allied forces liberated Austria from 1944-1945. The Austrian State Treaty (1955) restored independence with mandatory neutrality.',
    ],
  ],
  msc: [
    [
      'In what year did Leopold V, the Babenberg margrave, establish his line as independent rulers separate from the Holy Roman Emperor\'s direct authority?',
      [
        '976',
        '1018',
        '1055',
        '1122',
      ],
      0,
      'Leopold I (Liutpold) founded the Babenberg dynasty around 976 as margrave of the March of Austria under nominal imperial suzerainty.',
    ],
    [
      'What constitutional arrangement characterized the relationship between Austria and Hungary within the empire established by the Austro-Hungarian Compromise?',
      [
        'Dual monarchy with two crowns',
        'Unitary state with Budapest as sole capital',
        'Confederation under a single parliament',
        'Personal union only, with no shared sovereignty',
      ],
      0,
      'The Ausgleich (Compromise) of 1867 created a dual monarchy with Austria and Hungary as co-equal states sharing only foreign affairs.',
    ],
    [
      'Which Habsburg emperor convened the Congress of Vienna, reorganizing Europe after the Napoleonic Wars?',
      [
        'Francis I',
        'Joseph II',
        'Leopold II',
        'Charles VI',
      ],
      0,
      'Emperor Francis I, through his foreign minister Metternich, hosted and shaped the 1815 Congress of Vienna.',
    ],
    [
      'What was the primary reason the First Austrian Republic adopted a federal system of government in 1919?',
      [
        'To balance power between Vienna and the regions',
        'Because the treaty required it as a condition of peace',
        'To prepare for union with Germany',
        'To satisfy the League of Nations membership requirement',
      ],
      0,
      'Austria\'s new federal structure divided power among states, compensating for the republic\'s drastically reduced territory and population.',
    ],
    [
      'Which musical and cultural movement was closely associated with late-19th-century Vienna under Habsburg rule?',
      [
        'The Viennese Waltz tradition',
        'Impressionism in painting',
        'Romantic nationalism in literature',
        'German Expressionist theater',
      ],
      0,
      'The waltz and composers like Johann Strauss II epitomized Vienna\'s global cultural prestige and the sophisticated imperial court life.',
    ],
    [
      'In what year did the Nazi regime annex Austria, and what was the official German term for this action?',
      [
        '1938; Anschluss',
        '1936; Lebensraum',
        '1940; Reichsgaue',
        '1935; Gleichschaltung',
      ],
      0,
      'The 1938 Anschluss (union/connection) absorbed Austria into the German Reich under Hitler, ending Austrian independence.',
    ],
    [
      'Which of the following was NOT a major ethnic group seeking independence or autonomy within Austria-Hungary by the early 20th century?',
      [
        'Flemish',
        'Poles',
        'Romanians',
        'South Slavs',
      ],
      0,
      'Flemish nationalism was associated with Belgium, not Austria-Hungary. Poles, Romanians, and South Slavs all sought autonomy within the empire.',
    ],
    [
      'What was the Metternich System, and which era did it dominate?',
      [
        'Conservative order established 1815-1848',
        'Austrian parliamentary democracy from 1867-1914',
        'Habsburg military strategy used against Prussia',
        'Economic union between Austria and Germany',
      ],
      0,
      'Klemens von Metternich\'s diplomatic system maintained conservative order and Austrian hegemony in Central Europe until 1848.',
    ],
    [
      'Why did Austria reluctantly declare war on Serbia in 1914, initiating World War I in the Balkans?',
      [
        'Assassination of Archduke Franz Ferdinand',
        'Expansion of territorial claims in the Balkans',
        'Alliance obligation to Germany\'s invasion of Belgium',
        'Naval blockade imposed by Britain',
      ],
      0,
      'The June 1914 assassination of heir Franz Ferdinand prompted Austria\'s ultimatum to Serbia, triggering the cascade into World War I.',
    ],
    [
      'What economic and trading bloc did Austria join in 1972, reflecting a shift toward Western European integration?',
      [
        'EFTA',
        'European Economic Community (EEC)',
        'COMECON',
        'Benelux Union',
      ],
      0,
      'Austria joined EFTA in 1972, establishing trade ties with Western Europe while maintaining official neutrality during the Cold War.',
    ],
    [
      'Which medieval legal privilege elevated Austria from a margraviate to a ducal territory with special status?',
      [
        'Privilegium Minus',
        'Golden Bull of 1222',
        'Magna Carta of 1215',
        'Peace of Westphalia',
      ],
      0,
      'The Privilegium Minus (1156) granted Austria independence from Bavaria\'s overlordship, establishing it as a duchy under imperial protection.',
    ],
    [
      'What role did the Austrian State Treaty of 1955 play in ending post-World War II occupation?',
      [
        'Restored sovereignty in exchange for neutrality',
        'Divided Austria into separate zones under Soviet and Western control',
        'Required Austria to join NATO immediately',
        'Transferred Tyrol permanently to Italy',
      ],
      0,
      'The 1955 Austrian State Treaty restored full independence and sovereignty while constitutionalizing Austrian neutrality.',
    ],
    [
      'Emperor Joseph II pursued which major internal reform that modernized but destabilized the empire?',
      [
        'Enlightened absolutism, centralizing power',
        'Feudal devolution of power to local aristocrats',
        'Democratization through universal suffrage',
        'Weakening of the Catholic Church\'s influence',
      ],
      0,
      'Joseph II\'s Enlightened absolutism consolidated central authority but alienated nobles and ethnic groups, creating long-term instability.',
    ],
    [
      'Which Babenberg ruler elevated Vienna to prominence as a major trading and strategic center on the Danube?',
      [
        'Henry II Jasomirgott',
        'Leopold the Glorious',
        'Albert the Wise',
        'Frederick the Warrior',
      ],
      0,
      'Henry II Jasomirgott (1141-1177) made Vienna his capital and granted it extensive trading privileges, transforming it into a major center.',
    ],
    [
      'Why was the First Austrian Republic initially prevented from union with Germany, a goal favored by many Austrians in 1919?',
      [
        'The Treaty of Saint-Germain prohibited it',
        'Austria voluntarily chose to remain independent',
        'Germany refused the union proposal',
        'The League of Nations mandated against it',
      ],
      0,
      'The Treaty of Saint-Germain (1919) forbade the Anschluss to prevent German dominance, a prohibition later violated by Nazi Germany.',
    ],
  ],
  phd: [
    [
      'The Privilegium Minus of 1156 raised Austria\'s status primarily to accomplish what strategic goal for the Holy Roman Emperor?',
      [
        'A buffer state against Bavaria and Hungary',
        'Centralize German power against French aggression',
        'Establish a counterweight to the Papacy in Italy',
        'Provide a naval base for Mediterranean trade',
      ],
      0,
      'The privilege rewarded the Babenbergs\' military service while establishing an independent March Austria as a strategic buffer in the eastern frontier.',
    ],
    [
      'How did the ethnic composition and nationalist movements within Austria-Hungary contribute to its eventual dissolution?',
      [
        'Competing ethnic claims to statehood fragmented the empire',
        'Ethnic homogeneity paradoxically caused stagnation and civil war',
        'The empire had no nationalist movements until 1914',
        'Nationalism was entirely an external invention of the treaty powers',
      ],
      0,
      'Slavic, Hungarian, Romanian, and other groups sought autonomy for decades; WWI\'s losses accelerated these centrifugal forces into full secession.',
    ],
    [
      'What historiographical debate surrounds Joseph II\'s reign regarding administrative centralization and ethnic tensions?',
      [
        'Whether his reforms strengthened or destabilized the empire',
        'Whether he was influenced more by Enlightenment or by Counter-Reformation ideology',
        'Whether he deliberately planned the empire\'s dissolution',
        'Whether his military campaigns were strategically sound',
      ],
      0,
      'Scholars dispute whether Joseph\'s rationalizing absolutism was necessary modernization or catalyst for the nationalist resentments that fractured the empire.',
    ],
    [
      'The Austro-Hungarian Compromise of 1867 created a dual monarchy partly in response to which military defeat?',
      [
        'Prussian victory in the Seven Weeks\' War',
        'Ottoman conquest of Hungary in 1526',
        'Napoleonic victories under the French Empire',
        'British naval dominance in global trade',
      ],
      0,
      'Austria\'s 1866 loss to Prussia forced reform; the Ausgleich elevated Hungary to co-equal status to preserve the realm\'s stability.',
    ],
    [
      'Which aspect of Vienna\'s position as an imperial capital reinforced Austria\'s cultural and diplomatic influence despite its declining military-economic power after 1870?',
      [
        'Vienna\'s role as a cosmopolitan intellectual center',
        'Its location on major trade routes guaranteeing commercial dominance',
        'Direct military control of the Mediterranean',
        'Exclusive alliance with Britain and France',
      ],
      0,
      'Vienna\'s cultural prestige—music, philosophy, psychology, literature—sustained Habsburg soft power even as Prussian dominance grew.',
    ],
    [
      'How did the collapse of Austria-Hungary in 1918 reshape the political map of Central Europe, and what principle governed the territorial divisions?',
      [
        'National self-determination created new nation-states',
        'The victors simply redistributed lands as conquests',
        'Austria and Hungary remained intact but as occupied colonies',
        'No significant border changes occurred',
      ],
      0,
      'The 1919 treaties applied self-determination inconsistently, creating states like Czechoslovakia and Yugoslavia with sizable minorities, sowing future conflict.',
    ],
    [
      'Why did Austria adopt a quasi-presidential federal system in its 1920 constitution rather than a purely parliamentary one?',
      [
        'To create executive stability',
        'Because the treaty powers mandated it',
        'Because Austria had always used presidential systems',
        'To prepare for unification with Germany',
      ],
      0,
      'A powerful Presidency was intended to provide executive continuity; however, constitutional conflicts between Parliament and President persisted.',
    ],
    [
      'What was the Ständestaat, and how did it represent Austria\'s attempt to resist both Nazi and Soviet influence in the 1930s?',
      [
        'Austro-fascist corporate state under Dollfuß',
        'Socialist worker\'s movement against Nazi expansion',
        'Restoration of the Habsburg monarchy in exile',
        'International pacifist organization',
      ],
      0,
      'The 1934-1938 Ständestaat under Dollfuß tried to forge a Catholic authoritarian alternative to both Nazi totalitarianism and communism.',
    ],
    [
      'How did the Austrian Resistance movements during World War II differ from resistance in other occupied territories?',
      [
        'Limited by ambiguity over Austrian identity and status',
        'It was non-existent and Austria fully collaborated',
        'Austrians immediately liberated themselves without external help',
        'Austria had unified resistance across all political groups',
      ],
      0,
      'Austrians\' complex relationship to the 1938 Anschluss (some welcomed it) fractured resistance efforts during WWII occupation.',
    ],
    [
      'What role did the Austrian Communist Party play in the postwar Second Republic, and why did it remain marginal despite Soviet occupation?',
      [
        'Weak due to Catholic identity',
        'It seized power immediately after 1945',
        'Austria had no communist presence whatsoever',
        'The communist party dominated postwar politics',
      ],
      0,
      'Though the Soviets occupied eastern Austria, Austrian Catholicism and Western ties limited communist gains; the 1955 treaty further reduced Soviet leverage.',
    ],
    [
      'How is it possible that Austria\'s postwar military neutrality allowed it to join the EU in 1995 without joining NATO?',
      [
        'EU membership is compatible with military neutrality',
        'Austria secretly joined NATO in 1995',
        'Neutrality and EU membership are contradictory; Austria violated its treaty',
        'Austria is not actually a member of the EU',
      ],
      0,
      'The 1955 Austrian State Treaty tied neutrality only to military non-alignment; EU membership (economic and political) does not contradict this.',
    ],
    [
      'How did the Metternich System\'s collapse in 1848 relate to nationalist and liberal uprisings across the Habsburg domains?',
      [
        'Revolutions overwhelmed Metternich\'s system',
        'Revolutions did not occur in Austria',
        'Metternich successfully prevented all reform',
        'Nationalism did not emerge until 1900',
      ],
      0,
      '1848 uprisings across the empire attacked both autocracy and multinational hegemony; though suppressed, they exposed system fragility.',
    ],
    [
      'Which intellectuals and movements centered in Vienna during the late 19th and early 20th centuries are considered foundational to modern thought?',
      [
        'Freud, Mahler, Schönberg, and the Vienna Circle',
        'Only political theorists mattered to Vienna\'s influence',
        'Vienna produced no significant thinkers',
        'All important modernism originated in Germany',
      ],
      0,
      'Vienna was a nexus of psychological, musical, philosophical, and artistic innovation that reshaped 20th-century intellectual life globally.',
    ],
    [
      'How did Austria\'s geographic location between German and Ottoman-Balkan spheres shape its medieval development under the Babenbergs?',
      [
        'Required military strength and alliances',
        'Geography was irrelevant to Austrian development',
        'Austria was always under Ottoman rule',
        'Austria had no strategic importance',
      ],
      0,
      'The Danube corridor and imperial ties positioned Vienna as a vital frontier trading post and military stronghold, driving Babenberg expansion.',
    ],
    [
      'What historiographical reassessment has emerged regarding Austrian complicity in Nazi crimes and the postwar narrative of Austria as a "first victim"?',
      [
        'Austrian collaboration outweighs victim status',
        'Austria was entirely complicit with no victim elements',
        'Austria was entirely innocent with no collaboration',
        'Historical debate on this topic does not exist',
      ],
      0,
      'Since the 1980s, historians have challenged the "victim" narrative, documenting significant Austrian Nazi support and participation in war crimes.',
    ],
  ],
  professor: [
    [
      'In the historiography of the Babenberg dynasty, what evidence challenges the traditional narrative of continuous territorial expansion, and how do modern scholars revise periodization?',
      [
        'Non-linear consolidation with reversals',
        'No evidence challenges the expansion narrative',
        'Babenbergs had minimal territorial control',
        'Expansion was continuous and unopposed',
      ],
      0,
      'Paleographic and cartographic reanalysis shows the "margraviate" was fragmented; consolidation was slower and more contested than traditional Romantic-era historiography claimed.',
    ],
    [
      'How do postcolonial and transnational approaches reframe the Austro-Hungarian Empire\'s internal diversity, moving beyond the "prison of nations" metaphor?',
      [
        'Agency, hybridity, and shared imperial structures',
        'Postcolonial theory has no relevance to Austria-Hungary',
        'The "prison of nations" remains the only accurate frame',
        'Austria-Hungary was entirely homogeneous',
      ],
      0,
      'Recent scholarship (Judson, Rozenblit) highlights shared imperial project, elite cosmopolitanism, and negotiated identities rather than linear oppression.',
    ],
    [
      'What role did the Austro-Hungarian Common Army play in maintaining imperial cohesion, and how did linguistic and national loyalty tensions destabilize it?',
      [
        'An integrative institution strained by mass politics',
        'The army had no internal tensions',
        'The army was composed of a single ethnic group',
        'Linguistic differences did not affect military function',
      ],
      0,
      'The k.u.k. Army symbolized imperial unity but faced mounting mutinies, desertion, and command confusion in WWI as soldiers\' national loyalties superseded imperial obligation.',
    ],
    [
      'How do revisionist accounts of the 1918-1920 transition challenge the notion of inevitable empire collapse, and what contingencies might have altered outcomes?',
      [
        'Failed federalism and diplomatic missteps',
        'Collapse was entirely inevitable with no contingencies',
        'Historians have not studied the transition period',
        'Only one outcome was possible',
      ],
      0,
      'Scholars (Suppan, Taylor) argue various federalist and constitutional reforms might have stabilized the realm; collapse resulted partly from political miscalculation.',
    ],
    [
      'In what ways did the First Austrian Republic\'s territorial diminution affect national identity construction, and how did competing visions of "Austrianness" emerge?',
      [
        'An identity vacuum of competing visions',
        'Austrian identity was firmly established before 1918',
        'There was no identity debate in the republic',
        'All Austrians agreed on national identity',
      ],
      0,
      'The republic struggled between German national identity (union with Weimar), Catholic corporatism, and a distinctive Austrian patriotism; this fracturing enabled Nazi appeal.',
    ],
    [
      'How do recent transnational histories of antisemitism in Austria differ from earlier accounts that blamed external Nazification?',
      [
        'Indigenous antisemitism predating Hitler',
        'Austria had no antisemitic tradition before Nazism',
        'Antisemitism began only with Hitler\'s arrival',
        'Austria was entirely free of religious prejudice',
      ],
      0,
      'Historians (Rozenblit, Pollack) trace 19th-century Austro-Hungarian antisemitism through Lueger\'s Vienna; Nazism capitalized on preexisting hostility rather than creating it.',
    ],
    [
      'What historiographical debate surrounds Austria\'s 1938 Anschluss: should it be read as annexation, national unification, or something more complex?',
      [
        'Coercion, manipulation, and divided opinion',
        'The Anschluss was unanimous popular support',
        'It was pure foreign conquest with no Austrian participation',
        'There is no scholarly debate on this topic',
      ],
      0,
      'Post-1980s revisionism (Hanisch, Botz) complicates earlier "victim" narratives, documenting significant Austrian Nazi backing while acknowledging coercive measures.',
    ],
    [
      'How did Austrian Catholic intellectual traditions navigate the ideological crisis of the 1920s-1930s, particularly regarding church response to both communism and fascism?',
      [
        'A Catholic third way toward authoritarianism',
        'The Catholic Church remained politically neutral throughout',
        'Catholicism had no influence on Austrian politics',
        'The Church exclusively supported democracy',
      ],
      0,
      'Austro-Catholic thought developed corporatist alternatives to liberal democracy and socialism; Church backing for Dollfuß\'s Ständestaat reflected this ideological trajectory.',
    ],
    [
      'What role did Vienna\'s intellectual communities (Vienna Circle, Second Viennese School, Freudian circles) play in producing exiled intellectuals who shaped postwar allied reconstruction?',
      [
        'Forced emigration shaped occupation policy',
        'Vienna had no intellectual influence on postwar planning',
        'Emigration had no effects on allied policy',
        'Vienna intellectuals remained in Austria throughout',
      ],
      0,
      'Exiled Austrians in allied service (Morgenstern, Helmer) shaped occupation policies; cultural emigration networks influenced how allied powers understood Austria\'s postwar role.',
    ],
    [
      'In what ways does the Austrian State Treaty (1955) represent a unique Cold War settlement, and how did Soviet concessions differ from German and Korean divisions?',
      [
        'Consensual withdrawal, not division',
        'Austria was divided like Germany',
        'The Soviets gained territorial concessions',
        'The treaty resolved nothing',
      ],
      0,
      'The 1955 treaty stands anomalous: Soviets accepted Austrian independence with mandatory neutrality rather than pursue partition or satellite status as in Germany.',
    ],
    [
      'How do historians account for Austria\'s relatively peaceful postwar transition and rapid integration into democratic Western structures, compared to other successor states?',
      [
        'Catholic consensus and Marshall aid',
        'Austria faced greater challenges than other states',
        'The transition was entirely peaceful with no difficulties',
        'Austria did not integrate into Western structures',
      ],
      0,
      'Austria\'s Catholic-socialist grand coalition (1945-1966) and Western Cold War support overcame denazification tensions; postwar consensus enabled faster stabilization.',
    ],
    [
      'What interpretive frameworks have scholars applied to understand the Habsburg monarchy\'s long survival (roughly 1282-1918) relative to other European empires?',
      [
        'Dynastic resilience and marriage alliances',
        'The Habsburgs had no special advantages',
        'Only military force sustained the monarchy',
        'The Habsburg realm was always on the verge of collapse',
      ],
      0,
      'Historians emphasize adaptive governance, interethnic elite shared investment in empire, and Habsburgs\' strategic marriage strategy as structural factors enabling longevity.',
    ],
    [
      'How does the concept of "supranationalism" apply to late Austria-Hungary, and what attempts at constitutional reform in 1914-1918 sought to preserve the realm?',
      [
        'Popovici\'s "Greater Austria" federalism plan',
        'Supranationalism had no relevance to Austria-Hungary',
        'No constitutional reform projects existed',
        'The war began with constitutional consensus',
      ],
      0,
      'Interwar and wartime intellectuals imagined federal supranational structures; these remained unrealized as nationalist parties rejected compromise and military defeat foreclosed options.',
    ],
    [
      'What does the "Austrian question" in Cold War diplomatic history refer to, and why was Austria\'s reunification and neutralization treated as a model or precedent?',
      [
        'A template for German reunification, never applied',
        'There was no Austrian question in Cold War diplomacy',
        'Austria\'s status had no diplomatic significance',
        'Austria\'s situation directly resolved the German question',
      ],
      0,
      'The 1955 Austrian settlement suggested division could be reversed through negotiation and neutrality; Cold War rigidity prevented applying this model to Germany or Korea.',
    ],
    [
      'How have environmental and spatial historians reconceived Vienna\'s role in the Habsburg Empire, particularly regarding resource flows, infrastructure, and the city\'s expansion as imperial capital?',
      [
        'Vienna\'s growth relied on imperial resources',
        'Vienna\'s development was purely internal',
        'The city had minimal imperial importance',
        'Infrastructure played no role in imperial power',
      ],
      0,
      'Recent environmental history (Orban) traces Vienna\'s food/energy dependence on Danube grain and provincial resources, linking urban expansion to imperial domination and nationalist resentment.',
    ],
  ],
};
